import React, { FC } from 'react'
import { Paper } from '@mui/material'
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium'
import { WagnerProbe } from 'src/api'
import { toInfoColumn } from 'src/components/columns/infoColumn'
import { usePatchWagnerProbeRequest } from 'src/hooks/api'
import { useAddPopupMessage } from 'src/hooks/ui'
import { RapidRHColumnsDataGrid } from './tableColumns'

const gridSx = {
  border: 'none',
  '& .MuiDataGrid-columnHeaderTitleContainerContent': { paddingLeft: '5px', overflow: 'visible' },
  '& .MuiDataGrid-columnHeaderTitle': {
    whiteSpace: 'normal',
    lineHeight: 1,
    fontSize: '13px',
    fontWeight: 400,
    color: 'rgba(0, 0, 0, 0.6)',
    overflow: 'visible'
  }
}

const columns: GridColDef<WagnerProbe>[] = [
  // The list column self-links to the probe's own page — drop it here.
  toInfoColumn(RapidRHColumnsDataGrid.ProbeId, { renderCell: undefined, valueFormatter: ({ value }) => value || '-' }),
  toInfoColumn(RapidRHColumnsDataGrid.Label, { valueFormatter: ({ value }) => value || '-' }),
  toInfoColumn(RapidRHColumnsDataGrid.Contractor),
  toInfoColumn(RapidRHColumnsDataGrid.Project),
  toInfoColumn(RapidRHColumnsDataGrid.ProjectSection, { headerName: 'Project Section' }),
  toInfoColumn(RapidRHColumnsDataGrid.Sensor),
  RapidRHColumnsDataGrid.Comment
]

interface WagnerProbeInfoProps {
  wagnerProbe: WagnerProbe
  refetchWagnerProbe?: () => void
}

export const WagnerProbeInfo: FC<WagnerProbeInfoProps> = ({ wagnerProbe, refetchWagnerProbe }) => {
  const addMessage = useAddPopupMessage()
  const updateWagnerProbeRequest = usePatchWagnerProbeRequest()
  const gridMinWidth = columns.reduce((sum, column) => sum + (column.minWidth ?? 100), 0)

  const processRowUpdate = async (newRow: WagnerProbe, oldRow: WagnerProbe) => {
    const { comment: newComment } = newRow
    const { comment: oldComment } = oldRow
    const updatedRow: WagnerProbe = await new Promise((resolve) => {
      if (newComment !== oldComment) {
        updateWagnerProbeRequest.mutate(
          {
            probeId: newRow.probeId,
            payload: { comment: newComment ?? null }
          },
          {
            onSuccess: () => {
              addMessage({ text: 'The comment updated', type: 'success' })
              if (typeof refetchWagnerProbe === 'function') refetchWagnerProbe()
              resolve(newRow)
            },
            onError: (error) => {
              addMessage({ text: error?.message, type: 'error' })
              resolve(oldRow)
            }
          }
        )
      }
      return newRow
    })
    return updatedRow
  }

  return (
    <Paper sx={{ width: '100%', overflowX: 'auto' }}>
      <DataGridPremium<WagnerProbe>
        autoHeight
        disableRowSelectionOnClick
        hideFooter
        rows={[wagnerProbe]}
        columns={columns}
        getRowId={(row) => row.probeId}
        rowThreshold={2}
        columnThreshold={2}
        localeText={{ columnMenuSortAsc: 'Sort A-Z', columnMenuSortDesc: 'Sort Z-A' }}
        sx={{ ...gridSx, minWidth: gridMinWidth }}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={(error) => {
          if (error) addMessage({ text: error, type: 'error' })
        }}
      />
    </Paper>
  )
}
